import { cssValueToPixels } from '@ui/tokens/mathUtils';
import { getStringWidth } from '@visx/text';
import { Annotation, AnnotationLabel } from '@visx/xychart';
import { useMemo } from 'react';

import { AnyD3Scale, ScaleInput } from '@visx/scale';
import tokens from '../../../tokens/_variables.json';

// Visx type defs leave a little to be desired... Either dataKey or xAccessor
// & yAccessor must be defined for the annotation to be reliably positioned.
type DatumSpecifier<DatumT extends object> =
  | {
      dataKey: string;
    }
  | {
      xAccessor: (d: DatumT) => ScaleInput<AnyD3Scale>;
      yAccessor: (d: DatumT) => ScaleInput<AnyD3Scale>;
    };

type Props<DatumT extends object> = {
  barX: number;
  chartWidth: number;
  chartMarginLeft: number;
  datum: DatumT;
  title: string;
  color?: string;
  colorLightBackground?: string;
  subTitle?: string;
  labelClassName?: string;
} & DatumSpecifier<DatumT>;

const titleFontSize = cssValueToPixels(tokens['text-heading-s'].fontSize);

const titlePropsDefault = {
  fontFamily: tokens['text-heading-s'].fontFamily,
  fontSize: titleFontSize,
  fontWeight: tokens['text-heading-s'].fontWeight,
  lineHeight: cssValueToPixels(
    tokens['text-heading-s'].lineHeight,
    cssValueToPixels(tokens['text-heading-s'].fontSize),
  ),
  fill: tokens['color-notification-dark'],
  // Required for X-browser compatibility, as Safari renders Visx <Text />
  // with different vertical anchors to Chrome, resulting in less space
  // between the title and sub-title.
  verticalAnchor: 'middle' as const,
  // Offset by half the title line height
  dy: cssValueToPixels(tokens['text-heading-s'].fontSize),
};

const annotationPadding = { left: cssValueToPixels(tokens['spacing-xs']) };

const subtitleFontSize = tokens['text-supporting-bold'].fontSize;
const subtitleFontWeight = tokens['text-supporting-bold'].fontWeight;
const subtitlePropsDefault = {
  fontFamily: tokens['text-supporting-bold'].fontFamily,
  fontSize: subtitleFontSize,
  lineHeight: cssValueToPixels(
    tokens['text-supporting-bold'].lineHeight,
    cssValueToPixels(tokens['text-supporting-bold'].fontSize),
  ),
  fill: tokens.text.color.primary.body,
  verticalAnchor: 'middle' as const,
  dy: cssValueToPixels(tokens['text-heading-s'].fontSize) / 2 - cssValueToPixels(subtitleFontSize),
};

export const BarChartAnnotation = <DatumT extends object>({
  barX,
  chartWidth,
  chartMarginLeft,
  datum,
  title,
  subTitle,
  color,
  colorLightBackground,
  labelClassName,
  ...datumSpecifiers
}: Props<DatumT>) => {
  if (title == null && subTitle == null) {
    return <></>;
  }
  const titleWidth = getStringWidth(title, titlePropsDefault) ?? 0;
  const subtitleDx = titleWidth + 12;
  const annotationWidth = subTitle
    ? titleWidth + subtitleDx + (getStringWidth(subTitle, subtitlePropsDefault) ?? 0)
    : titleWidth;
  const spaceInBar = barX;
  const spaceOutsideBar = chartWidth - barX - annotationPadding.left;
  const labelPosition = annotationWidth > spaceInBar && spaceOutsideBar > annotationWidth ? 'right' : 'left';

  const fill = useMemo(
    () => (labelPosition === 'right' && colorLightBackground ? colorLightBackground : color),
    [labelPosition, color, colorLightBackground],
  );
  const titleProps = useMemo(
    () =>
      color
        ? {
            ...titlePropsDefault,
            fill,
            // If there is no subtitle, only adjust the y position by half the font size to center the title in the bar.
            dy: subTitle ? titlePropsDefault.dy : titleFontSize / 2,
          }
        : titlePropsDefault,
    [color, fill, subTitle],
  );
  const subTitleProps = useMemo(
    () =>
      color
        ? {
            ...subtitlePropsDefault,
            fill,
            dx: subtitleDx,
          }
        : subtitlePropsDefault,
    [color, fill, subtitleDx],
  );
  const x = useMemo(() => (labelPosition === 'right' ? undefined : chartMarginLeft), [labelPosition, chartMarginLeft]);

  return (
    <Annotation datum={datum} {...datumSpecifiers}>
      <AnnotationLabel
        title={title}
        showBackground={false}
        showAnchorLine={false}
        verticalAnchor="middle"
        horizontalAnchor="start"
        backgroundPadding={annotationPadding}
        x={x}
        // If this prop is not manually set to undefined, the Font size
        // set in titleProps is overwritten by the default of 12px, and
        // the vertical positioning breaks on Safari and Firefox 🤦‍♂️
        titleFontSize={undefined}
        titleProps={titleProps}
        subtitle={subTitle}
        subtitleFontSize={subtitleFontSize}
        subtitleFontWeight={subtitleFontWeight}
        subtitleProps={subTitleProps}
        // If a width is not explicitly set, it is implicitly set by visx
        // if the chart is narrow. Given text wrapping causes dy offset
        // issues (seehttps://oculatechnologies.atlassian.net/browse/OTP-5098),
        // text overflow is preferable.
        width={300}
        className={labelClassName}
      />
    </Annotation>
  );
};
